import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, Trophy, IndianRupee, ArrowUpRight, Calendar, Users, CheckCircle2 } from 'lucide-react';
import { SectionHeader } from './Services';

const prizes = [
  { icon: Trophy, label: '₹15,000 Scholarship', sub: 'Top-scoring students', color: '#e9c46a' },
  { icon: IndianRupee, label: '₹13,500 Scholarship', sub: 'High merit performers', color: '#c9c2a8' },
  { icon: IndianRupee, label: '₹10,000 Scholarship', sub: 'Qualified test scorers', color: '#c9c2a8' },
  { icon: IndianRupee, label: '₹5,000 Scholarship', sub: 'Eligible participants', color: '#c9c2a8' },
];

const eligibility = [
  'Graduates or final-year students in Commerce/BBA/CA/CMA',
  'Working professionals in accounting or tax',
  'Age 18 or above',
  'Basic English comprehension',
];

const pad = (n) => n.toString().padStart(2, '0');

const Scholarship = () => {
  const [time, setTime] = useState({ d: 0, h: 0, m: 0, s: 0 });

  useEffect(() => {
    // Test date: next Saturday from load, at 10am
    const target = new Date();
    const day = target.getDay();
    const daysToSat = (6 - day + 7) % 7 || 7;
    target.setDate(target.getDate() + daysToSat);
    target.setHours(10, 0, 0, 0);

    const tick = () => {
      const diff = Math.max(0, target - new Date());
      const d = Math.floor(diff / (1000 * 60 * 60 * 24));
      const h = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const m = Math.floor((diff / (1000 * 60)) % 60);
      const s = Math.floor((diff / 1000) % 60);
      setTime({ d, h, m, s });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <section className="relative py-20 md:py-28">
      <div className="squiggle-bg" />
      <div className="max-w-6xl mx-auto px-6 relative">
        <SectionHeader
          eyebrow="Scholarship Test"
          title={<>Win fixed <em className="italic">scholarship awards</em></>}
          description="Take our free 45-minute aptitude test and unlock scholarships from ₹5,000 to ₹15,000. Held every Saturday online."
        />

        {/* Main card */}
        <div className="card-soft rounded-2xl overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* Left: countdown + CTA */}
            <div className="p-8 md:p-10 border-b lg:border-b-0 lg:border-r border-[#143324]/10">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#143324]/15 bg-white/60 text-[11px] tracking-[0.18em] uppercase text-[#143324] font-body mb-6">
                <Calendar size={13} /> Next test in
              </div>

              <div className="grid grid-cols-4 gap-2 md:gap-3 mb-8 max-w-md">
                {[
                  { v: pad(time.d), l: 'Days' },
                  { v: pad(time.h), l: 'Hours' },
                  { v: pad(time.m), l: 'Mins' },
                  { v: pad(time.s), l: 'Secs' },
                ].map((c, i) => (
                  <div key={i} className="bg-[#143324] text-[#f5f1e6] rounded-xl px-2 py-3 md:px-3 md:py-4 text-center">
                    <div className="font-serif-display text-3xl md:text-4xl leading-none tabular-nums">{c.v}</div>
                    <div className="text-[10px] tracking-widest uppercase mt-1.5 text-[#c9c2a8] font-body">{c.l}</div>
                  </div>
                ))}
              </div>

              <h3 className="font-serif-display text-[#143324] text-3xl md:text-4xl leading-tight mb-3">
                Test structure <em className="italic">at a glance</em>
              </h3>
              <ul className="space-y-2.5 mb-8">
                {[
                  '45 mins • 50 MCQ questions',
                  'Sections: Aptitude, English, Accounting basics',
                  'Instant score after submission',
                  'Retake once after 30 days',
                ].map((t, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-[14px] text-[#1c1c1c] font-body">
                    <CheckCircle2 size={16} className="text-[#143324] mt-0.5" strokeWidth={2} />
                    {t}
                  </li>
                ))}
              </ul>

              <div className="flex items-center gap-3 flex-wrap">
                <Link to="/contact" className="inline-flex items-center gap-2 btn-primary px-6 py-3 rounded-full text-sm font-body">
                  Register for Free Test <ArrowUpRight size={15} />
                </Link>
                <Link to="/ea-course" className="inline-flex items-center gap-2 btn-outline px-6 py-3 rounded-full text-sm font-body">
                  Course details
                </Link>
              </div>

              <div className="mt-6 inline-flex items-center gap-2 text-[12px] text-[#3d3d3d] font-body">
                <Users size={13} className="text-[#143324]" /> 320+ registered this week
              </div>
            </div>

            {/* Right: prize tiers */}
            <div className="p-8 md:p-10 bg-[#f0ead9]/50">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-11 h-11 rounded-xl bg-[#143324] text-[#f5f1e6] flex items-center justify-center">
                  <GraduationCap size={20} strokeWidth={1.6} />
                </div>
                <div>
                  <p className="text-[11px] tracking-widest uppercase text-[#3d3d3d] font-body">Prize Pool</p>
                  <p className="font-body font-semibold text-[#143324]">Scholarships up to &#8377;15,000</p>
                </div>
              </div>

              <ul className="space-y-3 mb-8">
                {prizes.map((p, i) => {
                  const Icon = p.icon;
                  return (
                    <li
                      key={i}
                      className={`rounded-xl p-4 flex items-center gap-4 border ${
                        i === 0
                          ? 'bg-[#143324] text-[#f5f1e6] border-[#143324]'
                          : 'bg-white/60 border-[#143324]/10'
                      }`}
                    >
                      <div
                        className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                          i === 0 ? 'bg-[#f5f1e6]/15' : 'bg-[#f5f1e6]'
                        }`}
                      >
                        <Icon size={17} className={i === 0 ? 'text-[#e9c46a]' : 'text-[#143324]'} strokeWidth={1.7} />
                      </div>
                      <div className="flex-1">
                        <p className={`font-body font-semibold ${i === 0 ? '' : 'text-[#143324]'}`}>{p.label}</p>
                        <p className={`text-[12px] font-body ${i === 0 ? 'text-[#c9c2a8]' : 'text-[#3d3d3d]'}`}>{p.sub}</p>
                      </div>
                    </li>
                  );
                })}
              </ul>

              <div>
                <p className="text-[11px] tracking-widest uppercase text-[#3d3d3d] font-body mb-3">Who can apply</p>
                <ul className="space-y-2">
                  {eligibility.map((e, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-[13px] text-[#1c1c1c] font-body">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#143324] shrink-0" />
                      {e}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Scholarship;
